import { useState } from "react";
import { Button, Checkbox, HBLoadingSpinnerOnly, Modal, SelectField } from "~components";
import { Css } from "~generated/css";
import { AggregateVersionStatus, CheckoutConfigStatus, Maybe } from "~generated/graphqlTypes";
import { queryResult, useTestIds } from "~utils";
import {
  DebugModal_ProductOfferingFragment,
  DebugModal_VersionFragment,
  useDebugModal_LoadQuery,
  useDebugModal_SyncToVersionMutation,
} from "./DebugModal.generated";
import { useShowOfferingVersionBadge } from "./VersionBadge";

type DebugModalProps = {
  checkoutConfigId: string;
  onClose: () => void;
};

export function DebugModal({ checkoutConfigId, onClose }: DebugModalProps) {
  const query = useDebugModal_LoadQuery({ variables: { id: checkoutConfigId } });
  const [showOfferingVersionBadge, setShowOfferingVersionBadge] = useShowOfferingVersionBadge();
  const tid = useTestIds({});

  return (
    <Modal
      isOpen
      close={onClose}
      title="Config Details"
      // Cap to the viewport on smaller screens; the rows themselves scroll, i.e. with both a MH and an ADU POF.
      size={{ width: "560px", height: "auto", maxWidth: "100%", maxHeight: "calc(100% - 32px)" }}
    >
      {queryResult(query, ({ checkoutConfig: config }) => {
        const { productOffering: pof, aduProductOffering: aduPof, structure } = config;
        // Versions are frozen once the config is reserved, for both the main house and the ADU.
        const versionsDisabled =
          config.status === CheckoutConfigStatus.Reserved || config.status === CheckoutConfigStatus.Expired;
        return (
          <div css={Css.df.fdc.px3.pt2.pb4.$}>
            <DebugLinkRow name="Pricing Trace" url={config.pricingTraceUrl?.url} />
            <DebugLinkRow label="Structure" name={structure.name} url={structure.blueprintUrl.url} />
            {pof && (
              <ProductOfferingRows
                label="Product Offering"
                versionTestId="mhVersion"
                checkoutConfigId={checkoutConfigId}
                pof={pof}
                currentVersion={config.aggregateVersion}
                versionsDisabled={versionsDisabled}
              />
            )}
            {aduPof && (
              <ProductOfferingRows
                label="ADU Product Offering"
                versionTestId="aduVersion"
                checkoutConfigId={checkoutConfigId}
                pof={aduPof}
                currentVersion={config.aduAggregateVersion}
                versionsDisabled={versionsDisabled}
              />
            )}
            {/* A sticky display preference, i.e. persisted to localStorage rather than to the config */}
            <div css={Css.df.aic.gap2.py2.$}>
              <Checkbox
                selected={showOfferingVersionBadge}
                onClick={() => setShowOfferingVersionBadge(!showOfferingVersionBadge)}
                {...tid.showOfferingVersionBadge}
              />
              <span css={Css.t14.gray700.$}>Show offering version badge</span>
            </div>
          </div>
        );
      })}
    </Modal>
  );
}

type ProductOfferingRowsProps = {
  label: string;
  /** I.e. `mhVersion` or `aduVersion`, to keep the two version selectors distinguishable. */
  versionTestId: string;
  checkoutConfigId: string;
  /** Either the MH or the ADU POF, i.e. `aggregateVersions` + `aggregateCheckout` are always this POF's own. */
  pof: DebugModal_ProductOfferingFragment;
  currentVersion: Maybe<DebugModal_VersionFragment>;
  versionsDisabled: boolean;
};

/** The POF link + its version selector and design package links, i.e. for either the main house or the ADU. */
function ProductOfferingRows({
  label,
  versionTestId,
  checkoutConfigId,
  pof,
  currentVersion,
  versionsDisabled,
}: ProductOfferingRowsProps) {
  return (
    <>
      <DebugLinkRow label={label} name={pof.name} url={pof.blueprintUrl.url} />
      <div css={Css.pl3.df.fdc.$}>
        <VersionSelector
          testId={versionTestId}
          checkoutConfigId={checkoutConfigId}
          currentVersion={currentVersion}
          versions={pof.aggregateVersions}
          aggregateCheckoutId={pof.aggregateCheckout?.id}
          disabled={versionsDisabled}
        />
        <DebugLinkRow label="Plan Package" name={pof.planPackage.name} url={pof.planPackage.blueprintUrl.url} />
        <DebugLinkRow
          label="Interior Design Package"
          name={pof.interiorDesignPackage.name}
          url={pof.interiorDesignPackage.blueprintUrl.url}
        />
        <DebugLinkRow
          label="Exterior Design Package"
          name={pof.exteriorDesignPackage.name}
          url={pof.exteriorDesignPackage.blueprintUrl.url}
        />
      </div>
    </>
  );
}

type VersionSelectorProps = {
  testId: string;
  checkoutConfigId: string;
  currentVersion: Maybe<DebugModal_VersionFragment>;
  versions: DebugModal_VersionFragment[];
  aggregateCheckoutId: string | undefined;
  disabled: boolean;
};

/** Syncs the config to another version of `versions`' POF, i.e. the main house's or the ADU's. */
function VersionSelector({
  testId,
  checkoutConfigId,
  currentVersion,
  versions,
  aggregateCheckoutId,
  disabled,
}: VersionSelectorProps) {
  const [syncError, setSyncError] = useState<string>();
  const [syncToVersion, { loading: syncing }] = useDebugModal_SyncToVersionMutation({
    refetchQueries: ["ConfiguratorContext_LoadConfig", "DebugModal_Load"],
    onError: (err) => setSyncError(err.message),
    onCompleted: () => setSyncError(undefined),
  });

  // Hide versions older than the promoted checkout version, but always keep the ccfg's current version.
  const checkoutVersionNumber = versions.find((v) => v.id === aggregateCheckoutId)?.version ?? 0;
  const eligibleVersions = versions.filter(
    (v) => (v.version ?? 0) >= checkoutVersionNumber || v.id === currentVersion?.id,
  );

  const versionOptions = eligibleVersions.map((v) => {
    const isCheckout = v.id === aggregateCheckoutId;
    const label = v.status.code === AggregateVersionStatus.Draft ? `Draft of v${v.version}` : `v${v.version}`;
    const promotedInfo = v.promotedAt
      ? `Promoted on ${new Date(v.promotedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}${v.promotedBy ? ` by ${v.promotedBy.name}` : ""}`
      : undefined;
    const value = isCheckout ? `${label} (promoted)` : label;
    return { id: v.id, value, info: promotedInfo };
  });

  const currentLabel = versionOptions.find((o) => o.id === currentVersion?.id)?.value ?? "";

  async function handleChange(value: string) {
    const rpavId = versionOptions.find((o) => o.value === value)?.id;
    if (!rpavId || syncing || rpavId === currentVersion?.id) return;
    setSyncError(undefined);
    await syncToVersion({ variables: { input: { id: checkoutConfigId, rpavId } } });
  }

  return (
    <div css={Css.df.fdc.py1.bb.bcGray200.$}>
      <SelectField
        label={
          <div css={Css.df.aic.gap1.t12.gray.ttn.$}>
            <span>Version</span>
            {syncing && (
              <div css={Css.wPx(16).hPx(16).$}>
                <HBLoadingSpinnerOnly />
              </div>
            )}
          </div>
        }
        options={versionOptions}
        selectedOptions={currentLabel}
        onChange={handleChange}
        allowDeselect={false}
        disabled={disabled}
        data-testid={testId}
      />
      {syncError && <div css={Css.t12.red600.mt1.$}>{syncError}</div>}
    </div>
  );
}

type DebugLinkRowProps = { label?: string; name?: string; url: string | undefined };

function DebugLinkRow({ label, name, url }: DebugLinkRowProps) {
  if (!url) return null;
  return (
    <div css={Css.df.fdc.py1.bb.bcGray200.$}>
      <span css={Css.t12.gray.$}>{label}</span>
      <Button variant="link" href={url} openInNewTab xss={Css.jcfs.t14.fw500.pl0.$}>
        {name ?? label}
      </Button>
    </div>
  );
}
